import { ArrowRight, Users } from "@ews-admin/global-design-system";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { adminUsersApi } from "../../services/adminUsers.api";

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * First of the last 7 days, today included, as a yyyy-MM-dd platform day. The platform zone,
 * Africa/Dakar, is UTC all year, so the UTC date is the platform date.
 */
export function lastSevenDaysStart(now: Date = new Date()): string {
  return new Date(now.getTime() - 6 * DAY_MS).toISOString().slice(0, 10);
}

/**
 * MED-1341: new sign-ups of the last 7 days on Aperçu. Each figure links to the Utilisateurs page
 * filtered on exactly those accounts, so the count and the list always agree.
 */
export const NewSignUps: React.FC = () => {
  const { t } = useTranslation();
  const [since] = useState(() => lastSevenDaysStart());
  const [counts, setCounts] = useState<{
    accounts: number;
    patients: number;
  } | null>(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      adminUsersApi.search({ createdFrom: since }, 0, 1),
      adminUsersApi.search({ createdFrom: since, role: "PATIENT" }, 0, 1),
    ])
      .then(([accounts, patients]) => {
        if (!cancelled) {
          setCounts({
            accounts: accounts.totalElements,
            patients: patients.totalElements,
          });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLoadError(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [since]);

  const linkClass =
    "text-sm font-semibold text-primary-700 hover:text-primary-800 hover:underline";

  return (
    <section
      aria-label={t("admin.users.newSignUps.title")}
      className="flex flex-wrap gap-x-6 gap-y-2 items-center px-5 py-3 page-section card-container"
    >
      <div className="flex gap-3 items-center">
        <div className="flex justify-center items-center w-9 h-9 rounded-full bg-primary-100">
          <Users className="w-5 h-5 text-primary-600" />
        </div>
        <h3 className="text-sm font-semibold text-gray-900">
          {t("admin.users.newSignUps.title")}
        </h3>
      </div>
      {loadError ? (
        <p className="text-sm text-danger-700">
          {t("admin.users.newSignUps.loadError")}
        </p>
      ) : (
        <div className="flex flex-wrap gap-4 items-center">
          <Link to={`/admin/users?from=${since}`} className={linkClass}>
            {counts
              ? t("admin.users.newSignUps.accounts", { count: counts.accounts })
              : "—"}
          </Link>
          <Link
            to={`/admin/users?role=PATIENT&from=${since}`}
            className={linkClass}
          >
            {counts
              ? t("admin.users.newSignUps.patients", { count: counts.patients })
              : "—"}
          </Link>
        </div>
      )}
      <Link
        to="/admin/users"
        className="flex gap-1 items-center ml-auto text-sm text-gray-600 hover:text-primary-700"
      >
        {t("admin.users.newSignUps.viewAll")}
        <ArrowRight className="w-4 h-4" />
      </Link>
    </section>
  );
};
